import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Container, Message, ExclamationIcon, ChevronIcon } from './styles';

export interface EmergencyMessageProps {
  message?: string;
  onClick?: () => void;
}

const MINIMUM_EDGE_DISTANCE = 100;

const EmergencyMessage = ({ message, onClick }: EmergencyMessageProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [position, setPosition] = useState(Math.floor(window.innerHeight * 0.85));
  const [translate, setTranslate] = useState(110);

  const handleClick = () => {
    if (isDragging || isMoving || !onClick) return;
    onClick();
  };
  const handleDragStart = () => {
    setIsDragging(true);
    setIsMoving(false);
  };
  const handleDragMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!isDragging) return;
      setIsMoving(true);
      if (event instanceof MouseEvent) event.preventDefault();
      event.stopPropagation();

      let y = 0;
      if (event instanceof TouchEvent) {
        y = event.touches[0].clientY;
      } else {
        y = event.clientY;
      }

      const MAXIMUM_EDGE_DISTANCE = window.innerHeight - MINIMUM_EDGE_DISTANCE;
      if (y < MINIMUM_EDGE_DISTANCE) {
        setPosition(MINIMUM_EDGE_DISTANCE);
      } else if (y > MAXIMUM_EDGE_DISTANCE) {
        setPosition(MAXIMUM_EDGE_DISTANCE);
      } else {
        setPosition(y);
      }
    },
    [isDragging],
  );
  const handleDragStop = (event: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(false);
    if (isMoving) event.stopPropagation();
  };
  const handleReleaseAway = useCallback((event: MouseEvent) => {
    if (!ref.current) return;
    if (!event.target) return;
    if (!(event.target instanceof Element)) return;
    if (ref.current.contains(event.target)) return;

    setIsDragging(false);
  }, []);
  const handleWindowResize = useCallback(() => {
    setPosition(Math.floor(window.innerHeight * 0.85));
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleReleaseAway);
    return () => document.removeEventListener('mouseup', handleReleaseAway);
  }, [handleReleaseAway]);

  useEffect(() => {
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('touchmove', handleDragMove);
    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('touchmove', handleDragMove);
    };
  }, [handleDragMove]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [handleWindowResize]);

  useEffect(() => {
    setTimeout(() => setTranslate(0), 100);
  }, []);

  return (
    <Container
      ref={ref}
      style={{ top: position - 25, transform: `translateX(${translate}%)` }}
      onMouseDown={handleDragStart}
      onMouseUp={handleDragStop}
      onTouchStart={handleDragStart}
      onTouchEnd={handleDragStop}
      onClick={handleClick}
    >
      <ExclamationIcon />
      <Message>{message}</Message>
      {onClick && <ChevronIcon />}
    </Container>
  );
};

export default EmergencyMessage;
